import moment from "moment";
import type { EventChangeArg } from "@fullcalendar/core";
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { calendarKeys } from "../../../../stores/Calendar/calendar.keys";
import type { CalendarEvent } from "../adapters/types";

/**
 * Detects a no-op event change — e.g. FullCalendar's `revert()` after a failed
 * drag — where start, end and resource are all unchanged. Callers use this to
 * skip redundant persistence calls that would otherwise re-issue an identical
 * (and possibly bouncing) update. Shared by the main and single-therapist
 * calendar controllers.
 */
export function isNoOpEventChange(eventChangeInfo: EventChangeArg): boolean {
  const { oldEvent, event: newEvent } = eventChangeInfo;
  const isSameStart = oldEvent.start?.getTime() === newEvent.start?.getTime();
  const isSameEnd = oldEvent.end?.getTime() === newEvent.end?.getTime();
  const isSameResource =
    oldEvent.getResources()?.[0]?.id === newEvent.getResources()?.[0]?.id;
  return isSameStart && isSameEnd && isSameResource;
}

/**
 * Single-therapist (days-as-columns) view date correction.
 *
 * In that view every event is displayed shifted to `selectedDateStr` and grouped
 * into a per-day resource column whose id is the real ISO date. When persisting a
 * change we must restore the real date (taken from the column) and keep only the
 * time-of-day from the displayed event.
 */
export function correctEventDateTimeForSingleTherapistView(
  event: { start?: Date | string | null; end?: Date | string | null },
  actualDate: string | undefined,
): { correctedStart?: string; correctedEnd?: string } {
  const startTime = event.start ? moment(event.start).format("HH:mm:ss") : null;
  const endTime = event.end ? moment(event.end).format("HH:mm:ss") : null;
  const correctedStart =
    actualDate && startTime
      ? `${actualDate}T${startTime}`
      : event.start
        ? moment(event.start).toISOString()
        : undefined;
  const correctedEnd =
    actualDate && endTime
      ? `${actualDate}T${endTime}`
      : event.end
        ? moment(event.end).toISOString()
        : undefined;
  return { correctedStart, correctedEnd };
}

const DAY_FORMAT = "YYYY-MM-DD";

/**
 * Suffix marker for the per-day segment ids minted by
 * {@link projectEventsOntoDayColumns}. FullCalendar keys its event store by id
 * and keeps only the LAST input with a given id, so segments of one source event
 * must carry distinct ids or all but one silently vanish. Use
 * {@link sourceEventId} to get the persistable id back.
 */
const SEGMENT_ID_MARKER = "::day:";

/**
 * Strip the per-day segment suffix from an event id, yielding the id of the
 * underlying appointment / time slot. A no-op for unsplit events.
 */
export const sourceEventId = <T extends string | undefined>(id: T): T =>
  (typeof id === "string" ? (id.split(SEGMENT_ID_MARKER)[0] as T) : id);

/**
 * Single-therapist (days-as-columns) view layout — the inverse of
 * {@link correctEventDateTimeForSingleTherapistView}.
 *
 * Events arrive carrying their real dates. Each one is moved into the resource
 * column of its own date and time-shifted onto `selectedDateStr`, because
 * FullCalendar's resourceTimeGridDay only renders events that fall on the
 * currently displayed date.
 *
 * Events that cross midnight are split into one segment per covered day, each
 * clamped to its day. This is what makes leaves of absence visible: a vacation
 * is stored as a single row running from the first day 00:00 to the day *after*
 * the last day 00:00, so without splitting it collapsed into a zero-length event
 * at midnight in the first day's column (invisible) and every other day of the
 * vacation stayed empty.
 *
 * Only events that actually split get segment-suffixed ids; everything else
 * keeps its own id untouched. Handlers that persist a change must map the id
 * back with {@link sourceEventId} — `extendedProps` (appointmentId, timeSlotId)
 * is copied unchanged onto every segment.
 *
 * @param window Optional inclusive-start/exclusive-end day range of the visible
 *   columns. Segments outside it are skipped, so a multi-week vacation does not
 *   produce columns that do not exist.
 */
export function projectEventsOntoDayColumns(
  events: CalendarEvent[],
  selectedDateStr: string,
  window?: { start: string; end: string },
): CalendarEvent[] {
  const nextDayStr = moment(selectedDateStr).add(1, "day").format(DAY_FORMAT);
  const projected: CalendarEvent[] = [];

  events.forEach((event) => {
    if (!event.start) {
      projected.push(event);
      return;
    }

    const start = moment(event.start);
    const end = event.end ? moment(event.end) : null;
    // An end of exactly midnight belongs to the day before it, not to the day it
    // opens — otherwise every vacation would grow an empty trailing column.
    const lastDay = (end && end.isAfter(start) ? end.clone().subtract(1, "ms") : start)
      .clone()
      .startOf("day");

    const segments: CalendarEvent[] = [];

    for (const day = start.clone().startOf("day"); day.isSameOrBefore(lastDay); day.add(1, "day")) {
      const dateStr = day.format(DAY_FORMAT);
      if (window && (dateStr < window.start || dateStr >= window.end)) continue;

      const dayEnd = day.clone().add(1, "day");
      const segmentStart = moment.max(start, day);
      const segmentEnd = end ? moment.min(end, dayEnd) : null;

      segments.push({
        ...event,
        resourceId: dateStr,
        start: `${selectedDateStr}T${segmentStart.format("HH:mm:ss")}`,
        // A segment running to midnight is written as 00:00 of the day after the
        // displayed one, since "24:00:00" is not a parsable time.
        end: segmentEnd
          ? segmentEnd.isSame(dayEnd)
            ? `${nextDayStr}T00:00:00`
            : `${selectedDateStr}T${segmentEnd.format("HH:mm:ss")}`
          : event.end,
        // Copied so the per-segment flags set later (isOutsideWorkingHours) do
        // not leak between segments of the same source event.
        extendedProps: { ...event.extendedProps },
      });
    }

    if (segments.length > 1 && event.id) {
      segments.forEach((segment) => {
        segment.id = `${event.id}${SEGMENT_ID_MARKER}${segment.resourceId}`;
      });
    }

    projected.push(...segments);
  });

  return projected;
}

export type DateRangeCacheSnapshot = [QueryKey, unknown][];

/**
 * Snapshot every cached `appointmentsForDateRange` query (the single-therapist
 * view's data source) so a failed optimistic update can be rolled back. The main
 * appointment mutations only snapshot the `appointmentsForDate` cache, which is
 * inactive in the single-therapist view — hence this view-specific helper.
 */
export const snapshotDateRangeCaches = (
  queryClient: QueryClient,
): DateRangeCacheSnapshot =>
  queryClient.getQueriesData({
    queryKey: calendarKeys.appointmentsForDateRange.all(),
  });

/** Restore a snapshot captured by {@link snapshotDateRangeCaches}. */
export const restoreDateRangeCaches = (
  queryClient: QueryClient,
  snapshot: DateRangeCacheSnapshot,
) => {
  snapshot.forEach(([queryKey, data]) => {
    queryClient.setQueryData(queryKey, data);
  });
};
